import axios, { AxiosInstance } from 'axios';
import {
  AzureAuthResponse,
  AuthUrlResponse,
  User,
  AnalysisRequest,
  AnalysisJob,
  AnalysisResults,
  Report,
} from '../types';
import {
  ScheduledAnalysis,
  ScheduledAnalysisCreate,
  ScheduledAnalysisUpdate,
  QuickLaunch,
  QuickLaunchCreate,
  QuickLaunchUpdate,
  QuickLaunchButtons
} from '../types/scheduled';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // Always use the environment variable if set, otherwise use relative URL
  return process.env.REACT_APP_API_URL || '/api';
};

const API_BASE_URL = getApiBaseUrl();

function authRefreshUrl(): string {
  const base = API_BASE_URL.replace(/\/$/, '');
  if (base.startsWith('http')) {
    return `${base}/auth/refresh`;
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = base.startsWith('/') ? base : `/${base}`;
  return `${origin}${path}/auth/refresh`;
}

class ApiService {
  public client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    let refreshInFlight: Promise<string> | null = null;

    // Request interceptor - add authentication token from localStorage
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (config.headers && token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Development mode: Add X-Dev-User header for RBAC testing
        if (process.env.NODE_ENV === 'development') {
          const devUser = localStorage.getItem('dev_user');
          if (devUser && config.headers) {
            config.headers['X-Dev-User'] = devUser;
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response: on 401, refresh with Entra refresh_token (if present) and retry once
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config as typeof error.config & {
          _authRetry?: boolean;
        };
        if (status !== 401 || !originalRequest) {
          return Promise.reject(error);
        }

        const reqUrl = String(originalRequest.url ?? '');
        const onLoginFlow =
          typeof window !== 'undefined' &&
          (window.location.pathname === '/auth/login' ||
            window.location.pathname.startsWith('/auth/callback'));
        const isAuthBootstrap =
          reqUrl.includes('/auth/refresh') ||
          reqUrl.includes('/auth/login') ||
          reqUrl.includes('/auth/callback');

        const sendToLogin = () => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expires_at');
          if (
            typeof window !== 'undefined' &&
            window.location.pathname !== '/auth/login' &&
            !window.location.pathname.startsWith('/auth/callback')
          ) {
            window.location.href = '/auth/login';
          }
        };

        if (isAuthBootstrap) {
          if (!onLoginFlow) {
            sendToLogin();
          }
          return Promise.reject(error);
        }

        if (originalRequest._authRetry) {
          sendToLogin();
          return Promise.reject(error);
        }

        const rt = localStorage.getItem('refresh_token');
        if (!rt) {
          sendToLogin();
          return Promise.reject(error);
        }

        originalRequest._authRetry = true;

        try {
          if (!refreshInFlight) {
            refreshInFlight = (async () => {
              const { data } = await axios.post<{
                access_token: string;
                refresh_token?: string;
                expires_in?: number;
              }>(authRefreshUrl(), null, {
                params: { refresh_token: rt },
                timeout: 30000,
              });
              localStorage.setItem('access_token', data.access_token);
              if (data.refresh_token) {
                localStorage.setItem('refresh_token', data.refresh_token);
              }
              if (data.expires_in) {
                localStorage.setItem(
                  'token_expires_at',
                  String(Date.now() + data.expires_in * 1000)
                );
              }
              return data.access_token;
            })().finally(() => {
              refreshInFlight = null;
            });
          }
          const access = await refreshInFlight;
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return this.client.request(originalRequest);
        } catch {
          sendToLogin();
          return Promise.reject(error);
        }
      }
    );
  }

  async getSentinelCourses(): Promise<Record<string, string[]>> {
    const response = await this.client.get<Record<string, string[]>>(`/sentinel/courses`);
    return response.data;
  }

  async getSentinelStudents(courseId?: string): Promise<string[]> {
    const response = await this.client.get<string[]>(`/sentinel/students`, {
      params: {
        ...(courseId ? { course_id: courseId } : {}),
        include_both_types: true,
        use_lookup: true
      },
      timeout: 20000,
    });
    return response.data;
  }

  async getCourseDateRanges(): Promise<Record<string, { min_date: string; max_date: string }>> {
    const response = await this.client.get(`/pathfinder-postgres/course-date-ranges`);
    return response.data;
  }

  // Azure AD Authentication
  async getAuthUrl(): Promise<AuthUrlResponse> {
    const response = await this.client.get<AuthUrlResponse>('/auth/login');
    return response.data;
  }

  async handleAuthCallback(code: string): Promise<AzureAuthResponse> {
    const response = await this.client.get<AzureAuthResponse>(`/auth/callback?code=${code}`);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<AzureAuthResponse> {
    const response = await this.client.post<AzureAuthResponse>('/auth/refresh', null, {
      params: { refresh_token: refreshToken }
    });
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  async checkPermission(requiredRole: string): Promise<{
    has_permission: boolean;
    user_roles: string[];
    required_role: string;
    user_level: number;
    required_level: number;
  }> {
    const response = await this.client.get(`/auth/check-permission/${requiredRole}`);
    return response.data;
  }

  // Analysis Jobs
  async startAnalysis(request: AnalysisRequest): Promise<AnalysisJob> {
    const response = await this.client.post<AnalysisJob>('/analysis/start', request);
    return response.data;
  }

  async getAnalysisJobs(page: number = 0, limit: number = 12, search?: string): Promise<{
    jobs: AnalysisJob[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    
    const response = await this.client.get('/analysis/jobs', { params });
    return response.data;
  }

  async getAnalysisJob(jobId: number): Promise<AnalysisJob> {
    const response = await this.client.get<AnalysisJob>(`/analysis/jobs/${jobId}`);
    return response.data;
  }

  async getAllAnalysisJobs(): Promise<AnalysisJob[]> {
    // Fetch with large limit to get all jobs (including other users for Reports page)
    const response = await this.client.get('/analysis/jobs', {
      params: { page: 0, limit: 1000, all_users: true },
    });
    return response.data.jobs;
  }

  async downloadReport(
    jobId: number,
    format: 'html' | 'csv' | 'json',
    options?: { regenerate?: boolean }
  ): Promise<any> {
    const response = await this.client.get(`/analysis/jobs/${jobId}/download`, {
      params: {
        format,
        ...(options?.regenerate ? { regenerate: true } : {}),
      },
      responseType: format === 'json' ? 'json' : 'blob',
    });
    return response;
  }

  /**
   * URL for opening the interactive HTML report in a new tab.
   * Uses access_token query param because browser navigation cannot send Bearer headers.
   */
  getHtmlReportUrl(jobId: number, options?: { regenerate?: boolean }): string {
    const base = API_BASE_URL.replace(/\/$/, '');
    const path = `${base}/analysis/jobs/${jobId}/html-report`;
    const params = new URLSearchParams();
    if (options?.regenerate) {
      params.set('regenerate', 'true');
    }
    const token = localStorage.getItem('access_token');
    if (token) {
      params.set('access_token', token);
    }
    const qs = params.toString();
    if (base.startsWith('http')) {
      return qs ? `${path}?${qs}` : path;
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const apiPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${origin}${apiPath}`;
    return qs ? `${url}?${qs}` : url;
  }

  async getAnalysisResults(jobId: number): Promise<AnalysisResults> {
    const response = await this.client.get<AnalysisResults>(`/analysis/jobs/${jobId}/results`);
    return response.data;
  }

  async deleteAnalysisJob(jobId: number): Promise<void> {
    await this.client.delete(`/analysis/jobs/${jobId}`);
  }

  async fixStuckJobs(): Promise<{ message: string; fixed_jobs: number[] }> {
    const response = await this.client.post('/analysis/jobs/fix-stuck');
    return response.data;
  }

  // Paginated endpoints
  async getDwellLocations(jobId: number, page: number = 0, limit: number = 20, search?: string, deviceType?: string): Promise<{
    items: any[],
    total: number,
    page: number,
    limit: number,
    pages: number
  }> {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    
    const response = await this.client.get(`/analysis/jobs/${jobId}/dwell-locations`, { params });
    return response.data;
  }

  async getRoutes(jobId: number, page: number = 0, limit: number = 20, search?: string, deviceType?: string): Promise<{
    items: any[],
    total: number,
    page: number,
    limit: number,
    pages: number
  }> {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    
    const response = await this.client.get(`/analysis/jobs/${jobId}/routes`, { params });
    return response.data;
  }

  // Reports
  async getJobReports(jobId: number): Promise<Report[]> {
    const response = await this.client.get<Report[]>(`/reports/job/${jobId}`);
    return response.data;
  }

  async downloadAllReports(jobId: number): Promise<Blob> {
    const response = await this.client.get(`/reports/download-all/${jobId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Generate a formal report export (POST /reports/export) and return the
   * file blob plus the server-provided filename. Used by POL report exports
   * (template + format) and legacy custom section reports.
   */
  async exportCustomReport(request: {
    job_id: number;
    report_type: 'html_interactive' | 'pdf_summary' | 'csv_data' | 'json_data';
    sections?: string[];
    template?: 'executive_summary' | 'full_report' | 'entity_annex' | 'anomaly_digest';
    format_options?: Record<string, any>;
  }): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.post(
      '/reports/export',
      { sections: [], ...request },
      { responseType: 'blob' }
    );
    const disposition: string = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i);
    const filename = match?.[1] || `report_${request.job_id}`;
    return { blob: response.data, filename };
  }

  // Scheduled Analyses
  async getScheduledAnalyses(): Promise<ScheduledAnalysis[]> {
    const response = await this.client.get<ScheduledAnalysis[]>('/scheduled-analyses');
    return response.data;
  }

  async getScheduledAnalysis(scheduleId: number): Promise<ScheduledAnalysis> {
    const response = await this.client.get<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}`);
    return response.data;
  }

  async createScheduledAnalysis(data: ScheduledAnalysisCreate): Promise<ScheduledAnalysis> {
    const response = await this.client.post<ScheduledAnalysis>('/scheduled-analyses', data);
    return response.data;
  }

  async updateScheduledAnalysis(scheduleId: number, data: ScheduledAnalysisUpdate): Promise<ScheduledAnalysis> {
    const response = await this.client.patch<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}`, data);
    return response.data;
  }

  async toggleScheduledAnalysis(scheduleId: number, isActive: boolean): Promise<ScheduledAnalysis> {
    const response = await this.client.post<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}/toggle`, {
      is_active: isActive
    });
    return response.data;
  }

  async deleteScheduledAnalysis(scheduleId: number): Promise<void> {
    await this.client.delete(`/scheduled-analyses/${scheduleId}`);
  }

  async runScheduledAnalysisNow(scheduleId: number): Promise<{ message: string; job_id: number; schedule_id: number }> {
    const response = await this.client.post(`/scheduled-analyses/${scheduleId}/run-now`);
    return response.data;
  }

  // ========== QUICK LAUNCH ==========
  
  async getQuickLaunches(): Promise<QuickLaunch[]> {
    const response = await this.client.get<QuickLaunch[]>('/quick-launch');
    return response.data;
  }

  async createQuickLaunch(data: QuickLaunchCreate): Promise<QuickLaunch> {
    // Quick Launch creation should be fast (background work happens server-side).
    // Use a shorter timeout so the UI doesn't get stuck "Creating..." indefinitely.
    const response = await this.client.post<QuickLaunch>('/quick-launch', data, { timeout: 30000 });
    return response.data;
  }

  async getQuickLaunchButtons(quickLaunchId: number): Promise<QuickLaunchButtons> {
    const response = await this.client.get<QuickLaunchButtons>(`/quick-launch/${quickLaunchId}/buttons`);
    return response.data;
  }

  async updateQuickLaunch(quickLaunchId: number, data: QuickLaunchUpdate): Promise<QuickLaunch> {
    // Give more time for updates that trigger co-traveler reprocessing
    const response = await this.client.put<QuickLaunch>(`/quick-launch/${quickLaunchId}`, data, { timeout: 60000 });
    return response.data;
  }

  async deleteQuickLaunch(quickLaunchId: number): Promise<void> {
    await this.client.delete(`/quick-launch/${quickLaunchId}`);
  }

  // ========== COURSE MANAGEMENT ==========

  async getCourses(includeExpired = true, includeInactive = true): Promise<any> {
    const response = await this.client.get('/courses', { 
      params: { include_expired: includeExpired, include_inactive: includeInactive } 
    });
    return response.data;
  }

  async getActiveCourses(): Promise<any[]> {
    const response = await this.client.get('/courses/active');
    return response.data;
  }

  async checkCourseStatus(courseId: string): Promise<{
    exists: boolean;
    course_id: string;
    status: string;
    is_active?: boolean;
    is_expired?: boolean;
    is_current?: boolean;
    start_date?: string;
    end_date?: string;
    message: string;
  }> {
    const response = await this.client.get(`/courses/check/${encodeURIComponent(courseId)}`);
    return response.data;
  }

  async syncCourses(): Promise<any> {
    const response = await this.client.post('/courses/sync');
    return response.data;
  }

  async updateCourse(courseId: string, data: { 
    name?: string; 
    description?: string;
    start_date?: string;
    end_date?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.client.put(`/courses/${encodeURIComponent(courseId)}`, data);
    return response.data;
  }

  // Digital Pattern of Life
  async getDigitalPoL(params: {
    course_id?: string;
    entity_ids?: string;
    days?: number;
    start_date?: string;
    end_date?: string;
    activity_type?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await this.client.get('/pathfinder/digital-pol', { params });
    return response.data;
  }

  // User Preferences
  async getUserPreferences(): Promise<any> {
    const response = await this.client.get('/preferences/');
    return response.data;
  }

  async saveUserPreferences(preferences: any): Promise<any> {
    const response = await this.client.post('/preferences/', preferences);
    return response.data;
  }

  async updateUserPreferences(preferences: any): Promise<any> {
    const response = await this.client.put('/preferences/', preferences);
    return response.data;
  }

  async deleteUserPreferences(): Promise<void> {
    await this.client.delete('/preferences/');
  }

  // Global Module Settings (Settings -> Modules)
  async getModuleSettings(): Promise<any> {
    const response = await this.client.get('/module-settings');
    return response.data;
  }

  async updateModuleSettings(settings: any): Promise<any> {
    const response = await this.client.put('/admin/module-settings', settings);
    return response.data;
  }

}

export const apiService = new ApiService();
export default apiService;
